import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getJobById } from '../services/jobService';
import { saveJob, unsaveJob, isJobSaved } from '../services/savedJobsService';
import { applyForJob, checkExistingApplication } from '../services/applicationService';
import { getProfileStats } from '../services/seekerProfileService';
// import { checkPackageLimit } from '../services/subscriptionService';
import JobApplicationModal from '../components/JobApplicationModal';
import LoadingSpinner from '../components/LoadingSpinner';
import Footer from '../components/Footer';
import { getCompanyProfile } from '../services/companyService';

const formatLocation = (location) => {
  if (!location) return 'Location not specified';
  if (typeof location === 'string') return location;

  const parts = [];
  if (location.city) parts.push(location.city);
  if (location.state) parts.push(location.state);
  if (location.country) parts.push(location.country);
  if (location.remote) parts.push('Remote');
  if (location.hybrid) parts.push('Hybrid');

  return parts.length > 0 ? parts.join(', ') : 'Location not specified';
};

const toTitleCase = (value = '') => {
  if (!value) return '';
  return value
    .toString()
    .replace(/[_-]/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const parseNumeric = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatSalary = (salary) => {
  if (!salary) return 'Not specified';

  const minValue = parseNumeric(salary.min);
  const maxValue = parseNumeric(salary.max);
  const currency = salary.currency || 'INR';
  const period = salary.period || 'yearly';
  const isNegotiable = Boolean(salary.negotiable);

  const formatAmount = (amount) =>
    new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency
    }).format(amount);

  let rangeLabel = '';
  if (minValue && maxValue) {
    rangeLabel = `${formatAmount(minValue)} - ${formatAmount(maxValue)}`;
  } else if (minValue) {
    rangeLabel = `${formatAmount(minValue)}+`;
  } else if (maxValue) {
    rangeLabel = `Up to ${formatAmount(maxValue)}`;
  } else {
    rangeLabel = isNegotiable ? 'Negotiable' : 'Not specified';
  }

  const periodMap = {
    yearly: 'year',
    monthly: 'month',
    weekly: 'week',
    daily: 'day',
    hourly: 'hour'
  };

  const normalizedPeriod = typeof period === 'string' ? period.toLowerCase() : period;
  const periodLabel = periodMap[normalizedPeriod] || period;

  if (rangeLabel !== 'Not specified' && rangeLabel !== 'Negotiable' && periodLabel) {
    rangeLabel = `${rangeLabel} / ${periodLabel}`;
  }

  if (isNegotiable && !rangeLabel.includes('Negotiable')) {
    rangeLabel = `${rangeLabel} (Negotiable)`;
  }

  return rangeLabel;
};

const resolveDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'number') {
    const dateFromNumber = new Date(value);
    return Number.isNaN(dateFromNumber.getTime()) ? null : dateFromNumber;
  }
  if (typeof value === 'string') {
    const dateFromString = new Date(value);
    return Number.isNaN(dateFromString.getTime()) ? null : dateFromString;
  }
  if (typeof value.toDate === 'function') {
    const firebaseDate = value.toDate();
    return firebaseDate instanceof Date && !Number.isNaN(firebaseDate.getTime()) ? firebaseDate : null;
  }
  return null;
};

const experienceLevelLabels = {
  entry: 'Entry Level',
  mid: 'Mid Level',
  senior: 'Senior Level',
  executive: 'Executive Level'
};

const JobDetail = ({ user, userData }) => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showJobApplicationModal, setShowJobApplicationModal] = useState(false);
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const [companyProfile, setCompanyProfile] = useState(null);

  useEffect(() => {
    fetchJobDetails();
  }, [jobId]);

  useEffect(() => {
    if (user && jobId) {
      checkIfJobSaved();
      checkIfJobApplied();
    }
  }, [user, jobId]);

  const fetchJobDetails = async () => {
    try {
      const result = await getJobById(jobId);
      if (result.success) {
        setJob(result.job);
      } else {
        console.error('Failed to fetch job details:', result.error);
      }
    } catch (error) {
      console.error('Error fetching job details:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkIfJobSaved = async () => {
    try {
      const result = await isJobSaved(user.uid, jobId);
      if (result.success) {
        setSaved(result.isSaved);
      }
    } catch (error) {
      console.error('Error checking if job is saved:', error);
    }
  };

  const checkIfJobApplied = async () => {
    try {
      const result = await checkExistingApplication(jobId, user.uid);
      setApplied(result.exists);
    } catch (error) {
      console.error('Error checking if job is applied:', error);
    }
  };

  const fetchCompanyProfileData = async (companyId) => {
    try {
      const profileResult = await getCompanyProfile(companyId);
      if (profileResult.success) {
        setCompanyProfile(profileResult.data);
      } else {
        setCompanyProfile(null);
      }
    } catch (error) {
      console.error('Error fetching company profile:', error);
    }
  };

  useEffect(() => {
    if (job?.userId) {
      fetchCompanyProfileData(job.userId);
    } else {
      setCompanyProfile(null);
    }
  }, [job?.userId]);

  const isJobSeeker = userData?.userType === 'jobseeker';

  const handleApply = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (userData?.userType !== 'jobseeker') {
      return;
    }

    if (applied) {
      // If already applied, navigate to application history
      navigate('/application-history');
      return;
    }

    // Subscription checks disabled temporarily to allow free applications for job seekers.
    // console.log('🔍 [JobDetail] Checking subscription and application limit...');
    // try {
    //   const limitCheck = await checkPackageLimit(user.uid, 'application');
    // 
    //   if (!limitCheck.success) {
    //     if (limitCheck.needsSubscription) {
    //       const confirmPurchase = window.confirm(
    //         '❌ No Active Subscription\n\n' +
    //         'You need an active subscription to apply for jobs.\n\n' +
    //         'Would you like to view our packages?'
    //       );
    //       if (confirmPurchase) {
    //         navigate('/packages');
    //       }
    //       return;
    //     } else if (limitCheck.limitReached) {
    //       const confirmUpgrade = window.confirm(
    //         '❌ Application Limit Reached\n\n' +
    //         `You have used all ${limitCheck.max} applications in your current package.\n\n` +
    //         'Would you like to upgrade your package?'
    //       );
    //       if (confirmUpgrade) {
    //         navigate('/packages');
    //       }
    //       return;
    //     } else {
    //       alert('Error: ' + limitCheck.error);
    //       return;
    //     }
    //   }
    //
    //   console.log(`✅ [JobDetail] Application limit check passed. Remaining: ${limitCheck.remaining}`);
    //
    // } catch (error) {
    //   console.error('❌ [JobDetail] Error checking subscription:', error);
    //   alert('Failed to verify subscription. Please try again.');
    //   return;
    // }

    // Check if profile is complete before showing modal
    try {
      const profileStats = await getProfileStats(user.uid);
      if (profileStats.success && profileStats.stats.completionPercentage < 85) {
        if (window.confirm('Your profile is not complete. You need at least 85% completion to apply for jobs. Would you like to complete your profile now?')) {
          navigate('/seeker-profile');
        }
        return;
      }
    } catch (error) {
      console.error('Error checking profile completion:', error);
    }

    // Show application modal
    setShowJobApplicationModal(true);
  };

  const handleConfirmApplication = async (applicationData) => {
    if (!job) return;

    setApplying(true);
    try {
      const result = await applyForJob(job.id, user.uid, applicationData);

      if (result.success) {
        setShowJobApplicationModal(false);
        setApplied(true);
        setShowSuccessAnimation(true);

        // Re-check application status to ensure UI is updated
        setTimeout(async () => {
          await checkIfJobApplied();
        }, 500);

        // Hide success animation after 3 seconds
        setTimeout(() => {
          setShowSuccessAnimation(false);
        }, 3000);
      } else {
        alert(result.error || 'Failed to submit application');
      }
    } catch (error) {
      console.error('Error applying for job:', error);
      alert('Failed to submit application. Please try again.');
    } finally {
      setApplying(false);
    }
  };

  const handleSave = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      if (saved) {
        const result = await unsaveJob(user.uid, jobId);
        if (result.success) {
          setSaved(false);
        } else {
          console.error('Error unsaving job:', result.error);
        }
      } else {
        const result = await saveJob(user.uid, jobId);
        if (result.success) {
          setSaved(true);
        } else {
          console.error('Error saving job:', result.error);
        }
      }
    } catch (error) {
      console.error('Error updating saved job status:', error);
      alert('Failed to update saved jobs. Please try again.');
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  const postedDate = resolveDate(job?.publishedAt || job?.postedDate || job?.createdAt);
  const postedDateLabel = postedDate ? postedDate.toLocaleDateString() : 'Not specified';
  const salaryLabel = job?.salary ? formatSalary(job.salary) : (job?.salaryRange || 'Not specified');
  const experienceLabel = job?.experience || experienceLevelLabels[job?.experienceLevel] || 'Not specified';
  const workModeLabel = job?.workMode ? toTitleCase(job.workMode) : 'Not specified';
  const companyDetails = companyProfile || job?.companyDetails || {};
  const companyDescription = job?.companyDescription || companyDetails.description;
  const companyEmail = companyDetails.email;
  const companyPhone = companyDetails.phone;
  const companyWebsite = companyDetails.website;
  const companyAddressParts = [companyDetails.address, companyDetails.city, companyDetails.state, companyDetails.country, companyDetails.zipCode]
    .filter(Boolean);
  const companyAddress = companyAddressParts.length > 0 ? companyAddressParts.join(', ') : null;
  const contactDetailsList = [
    { label: 'Email', value: companyEmail, type: 'email' },
    { label: 'Phone', value: companyPhone, type: 'phone' },
    { label: 'Website', value: companyWebsite, type: 'website' },
    { label: 'Address', value: companyAddress, type: 'text' }
  ];

  if (!job) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Job not found</h2>
          <button
            onClick={() => navigate('/jobs')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Browse Jobs
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Job Header */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-6">
            <div className="p-6 sm:p-8">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                <div className="flex-1">
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">{job.title}</h1>
                  <div className="flex flex-wrap items-center gap-4 text-gray-600 mb-4">
                    <div className="flex items-center">
                      <svg className="w-5 h-5 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                      <span>{job.company}</span>
                    </div>
                    <div className="flex items-center">
                      <svg className="w-5 h-5 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span>{formatLocation(job.location)}</span>
                    </div>
                    <div className="flex items-center">
                      <svg className="w-5 h-5 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <span>{job.jobType}</span>
                    </div>
                  </div>
                </div>
                <div className="mt-4 md:mt-0 flex flex-col sm:flex-row gap-3">
                  {isJobSeeker && (
                    <button
                      onClick={handleApply}
                      disabled={applying}
                      className={`flex items-center gap-2 px-6 py-2 font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm ${applied ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
                    >
                      {applying ? (
                        <>
                          <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span>Applying...</span>
                        </>
                      ) : (
                        <>
                          <span>Apply Now</span>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                          </svg>
                        </>
                      )}
                    </button>
                  )}

                  <button
                    onClick={handleSave}
                    className={`flex items-center gap-2 px-4 py-2 border rounded-lg font-semibold transition-colors text-sm ${saved
                      ? 'border-red-500 text-red-600 bg-red-50 hover:bg-red-100'
                      : 'border-gray-300 text-gray-700 hover:border-red-500 hover:text-red-600'
                      }`}
                  >
                    <svg className="w-4 h-4" fill={saved ? "currentColor" : "none"} stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                    <span>{saved ? 'Saved' : 'Save Job'}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Job Details Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Job Description */}
              <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Job Description</h2>
                {job.description ? (
                  <div className="prose max-w-none text-gray-600 whitespace-pre-wrap">
                    {job.description}
                  </div>
                ) : (
                  <p className="text-gray-500 italic">No description provided.</p>
                )}
              </div>

              {/* Requirements */}
              <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Requirements</h2>
                <ul className="list-disc list-inside space-y-2 text-gray-600">
                  {job.requirements?.map((req, index) => (
                    <li key={index}>{req}</li>
                  ))}
                </ul>
              </div>

              {/* Responsibilities */}
              <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Responsibilities</h2>
                <ul className="list-disc list-inside space-y-2 text-gray-600">
                  {job.responsibilities?.map((resp, index) => (
                    <li key={index}>{resp}</li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Job Overview */}
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Job Overview</h2>
                <div className="space-y-4">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                      <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Posted Date</p>
                      <p className="font-medium">{postedDateLabel}</p>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                      <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Salary Range</p>
                      <p className="font-medium">{salaryLabel}</p>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
                      <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Experience</p>
                      <p className="font-medium">{experienceLabel}</p>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center mr-3">
                      <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Work Mode</p>
                      <p className="font-medium">{workModeLabel}</p>
                    </div>
                  </div>
                </div>

              </div>

              {/* Company Info */}
              {/* <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">About Company</h2>
              <div className="text-gray-600 space-y-4">
                <p>{companyDescription || 'Company description not available'}</p>

                <div className="space-y-2">
                  {contactDetailsList.map(({ label, value, type }) => {
                    let content = <span className="text-gray-400">Not provided</span>;

                    if (value) {
                      if (type === 'email') {
                        content = (
                          <a href={`mailto:${value}`} className="text-blue-600 hover:text-blue-800 break-all">
                            {value}
                          </a>
                        );
                      } else if (type === 'phone') {
                        content = (
                          <a href={`tel:${value}`} className="text-blue-600 hover:text-blue-800">
                            {value}
                          </a>
                        );
                      } else if (type === 'website') {
                        const normalizedUrl = value.startsWith('http') ? value : `https://${value}`;
                        content = (
                          <a
                            href={normalizedUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 break-all"
                          >
                            {value}
                          </a>
                        );
                      } else {
                        content = <span className="text-gray-700">{value}</span>;
                      }
                    }

                    return (
                      <div key={label} className="flex items-start gap-2 text-sm">
                        <span className="text-gray-500 min-w-[80px]">{label}:</span>
                        {content}
                      </div>
                    );
                  })}
                </div>

                <Link 
                  to={`/companies/${job.userId}`}
                  className="inline-block text-blue-600 hover:text-blue-800 font-medium"
                >
                  View Company Profile →
                </Link>
              </div>
            </div> */}
            </div>
          </div>
        </div>
      </div>

      {/* Success Animation */}
      {showSuccessAnimation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 transition-opacity duration-300">
          <div className="bg-white rounded-2xl p-8 max-w-md mx-4 text-center shadow-2xl transform transition-all duration-300 scale-100">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">🎉 Application Submitted!</h3>
            <p className="text-gray-600 mb-4">Your application has been sent successfully. Good luck!</p>
            <div className="flex items-center justify-center space-x-1">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
            </div>
          </div>
        </div>
      )}

      {/* Job Application Modal */}
      <JobApplicationModal
        job={job}
        isOpen={showJobApplicationModal}
        onClose={() => setShowJobApplicationModal(false)}
        onConfirm={handleConfirmApplication}
        applying={applying}
      />

      {/* Footer */}
      <Footer />
    </>
  );
};

export default JobDetail;
